<script lang="ts">
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import * as Card from '@/components/ui/card';
    import { Button } from '@/components/ui/button';
    import { Badge } from '@/components/ui/badge';
    import { Textarea } from '@/components/ui/textarea';
    import { router, useForm } from '@inertiajs/svelte';
    import { toast } from 'svelte-sonner';
    import {
        Mail,
        MailOpen,
        ArrowLeft,
        Trash,
        User,
        Calendar,
        MessageSquare,
        Send,
        ExternalLink
    } from 'lucide-svelte';

    type UserType = {
        id: string;
        name: string;
    };

    type Comment = {
        id: string;
        comment: string;
        created_at: string;
        user: UserType;
    };

    type Message = {
        id: string;
        title: string;
        body: string;
        priority: 'low' | 'medium' | 'high';
        is_read: boolean;
        read_at: string | null;
        created_at: string;
        creator: { name: string };
        assignee?: UserType;
        related_record_url?: string | null;
        related_record_label?: string | null;
        related_record_type_name?: string | null;
        comments: Comment[];
    };

    type Props = {
        message: Message;
    };

    let { message }: Props = $props();

    const breadcrumbs: BreadcrumbItem[] = [
        { title: 'Activity', href: '/activity' },
        { title: message.title, href: `/messages/${message.id}` },
    ];

    function getPriorityColor(priority: string) {
        switch (priority) {
            case 'high': return 'destructive';
            case 'medium': return 'default';
            case 'low': return 'secondary';
            default: return 'default';
        }
    }

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function goBack() {
        router.visit('/activity');
    }

    function deleteMessage() {
        if (confirm('Are you sure you want to delete this message?')) {
            router.delete(`/messages/${message.id}`);
        }
    }

    function markAsRead() {
        router.post(`/messages/${message.id}/mark-as-read`, {}, {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Message marked as read');
            }
        });
    }

    // Comment form
    let commentForm = useForm({
        comment: ''
    });

    function submitComment() {
        $commentForm.post(`/messages/${message.id}/comments`, {
            preserveScroll: true,
            onSuccess: () => {
                $commentForm.reset();
                toast.success('Comment added');
            }
        });
    }
</script>

<svelte:head>
    <title>{message.title} - Message</title>
</svelte:head>

<AppLayout {breadcrumbs}>
    <div class="p-4 max-w-4xl mx-auto space-y-4">
        <!-- Header Actions -->
        <div class="flex items-center justify-between">
            <Button variant="ghost" size="sm" onclick={goBack}>
                <ArrowLeft class="h-4 w-4 mr-2" />
                Back to Activity
            </Button>
            <div class="flex gap-2">
                {#if !message.is_read}
                    <Button variant="outline" size="sm" onclick={markAsRead}>
                        <MailOpen class="h-4 w-4 mr-2" />
                        Mark as Read
                    </Button>
                {/if}
                <Button variant="destructive" size="sm" onclick={deleteMessage}>
                    <Trash class="h-4 w-4 mr-2" />
                    Delete
                </Button>
            </div>
        </div>

        <!-- Main Message Card -->
        <Card.Root>
            <Card.Header>
                <div class="flex items-start gap-4">
                    <div class="mt-1">
                        {#if message.is_read}
                            <MailOpen class="h-6 w-6" style="color: var(--activity-complete);" />
                        {:else}
                            <Mail class="h-6 w-6" style="color: var(--error);" />
                        {/if}
                    </div>
                    <div class="flex-1">
                        <div class="flex items-center gap-2 mb-2">
                            <Badge variant={getPriorityColor(message.priority)}>
                                {message.priority} priority
                            </Badge>
                            <Badge
                                variant="secondary"
                                style="background-color: var({message.is_read ? '--activity-complete' : '--error'}); color: white;"
                            >
                                {message.is_read ? 'Read' : 'Unread'}
                            </Badge>
                        </div>
                        <Card.Title class="text-xl">{message.title}</Card.Title>
                    </div>
                </div>
            </Card.Header>
            <Card.Content class="space-y-4">
                <!-- Message Body -->
                <div class="prose prose-sm max-w-none">
                    <p class="whitespace-pre-wrap">{message.body}</p>
                </div>

                <!-- Meta Information -->
                <div class="border-t pt-4 space-y-2 text-sm text-muted-foreground">
                    <div class="flex items-center gap-2">
                        <User class="h-4 w-4" />
                        <span>From: <strong class="text-foreground">{message.creator.name}</strong></span>
                    </div>
                    {#if message.assignee}
                        <div class="flex items-center gap-2">
                            <User class="h-4 w-4" />
                            <span>To: <strong class="text-foreground">{message.assignee.name}</strong></span>
                        </div>
                    {/if}
                    <div class="flex items-center gap-2">
                        <Calendar class="h-4 w-4" />
                        <span>Sent: {formatDate(message.created_at)}</span>
                    </div>
                    {#if message.read_at}
                        <div class="flex items-center gap-2">
                            <MailOpen class="h-4 w-4" />
                            <span>Read: {formatDate(message.read_at)}</span>
                        </div>
                    {/if}
                </div>

                <!-- Related Record -->
                {#if message.related_record_label}
                    <div class="border-t pt-4">
                        <div class="flex items-center gap-2 text-sm text-muted-foreground">
                            <ExternalLink class="h-4 w-4" />
                            <span>Related to:</span>
                            {#if message.related_record_url}
                                <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- URL pre-resolved from backend -->
                                <a href={message.related_record_url}
                                    class="text-primary hover:underline font-medium"
                                >
                                    {#if message.related_record_type_name}
                                        <span>{message.related_record_type_name}:</span>
                                    {/if}
                                    {message.related_record_label}
                                </a>
                            {:else}
                                <strong class="text-foreground">
                                    {#if message.related_record_type_name}{message.related_record_type_name}: {/if}{message.related_record_label}
                                </strong>
                            {/if}
                        </div>
                    </div>
                {/if}
            </Card.Content>
        </Card.Root>

        <!-- Comments Section -->
        <Card.Root>
            <Card.Header>
                <Card.Title class="flex items-center gap-2">
                    <MessageSquare class="h-5 w-5" />
                    Comments ({message.comments?.length || 0})
                </Card.Title>
            </Card.Header>
            <Card.Content class="space-y-4">
                <!-- Existing Comments -->
                {#if message.comments && message.comments.length > 0}
                    <div class="space-y-3">
                        {#each message.comments as comment (comment.id)}
                            <div class="border rounded-lg p-3">
                                <div class="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                                    <User class="h-4 w-4" />
                                    <strong class="text-foreground">{comment.user.name}</strong>
                                    <span>&bull;</span>
                                    <span>{formatDate(comment.created_at)}</span>
                                </div>
                                <p class="text-sm whitespace-pre-wrap">{comment.comment}</p>
                            </div>
                        {/each}
                    </div>
                {:else}
                    <p class="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                {/if}

                <!-- Add Comment Form -->
                <form onsubmit={(e) => { e.preventDefault(); submitComment(); }} class="space-y-3">
                    <Textarea
                        bind:value={$commentForm.comment}
                        placeholder="Add a comment..."
                        rows={3}
                    />
                    <Button
                        type="submit"
                        size="sm"
                        disabled={!$commentForm.comment || $commentForm.processing}
                    >
                        <Send class="h-4 w-4 mr-2" />
                        Add Comment
                    </Button>
                </form>
            </Card.Content>
        </Card.Root>
    </div>
</AppLayout>